import { Button } from 'design-system';
import {
  Clock as ClockCircleOutlined,
  X as CloseOutlined,
  MapPin as EnvironmentOutlined,
  File as FileOutlined,
  ChevronRight as RightOutlined,
  User as UserOutlined,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CustomIcon from '../icons/CustomIcon';
import './search.css';

export default function SearchPanel({
  query,
  hasQuery,
  loading,
  results,
  recent,
  onPick,
  onRemoveRecent,
  onClearRecent,
  onOpenFull,
}) {
  const intl = useIntl();
  const [idx, setIdx] = useState(0);
  const items = hasQuery ? results : recent.map((r) => ({ ...r, title: r.q }));
  const indexedResults = results.map((item, index) => ({ item, index }));
  const resultSections = [
    {
      key: 'members',
      title: intl.formatMessage({ id: 'search_tab_members', defaultMessage: 'People' }),
      entries: indexedResults.filter(({ item }) => item.type === 'member'),
    },
    {
      key: 'graves',
      title: intl.formatMessage({ id: 'search_tab_graves', defaultMessage: 'Graves' }),
      entries: indexedResults.filter(({ item }) => item.type === 'grave'),
    },
    {
      key: 'other',
      title: intl.formatMessage({ id: 'search_suggestions_title', defaultMessage: 'Suggestions' }),
      entries: indexedResults.filter(({ item }) => !['member', 'grave'].includes(item.type)),
    },
  ].filter((section) => section.entries.length > 0);

  useEffect(() => setIdx(0), [query, loading]);

  useEffect(() => {
    function onKey(e) {
      if (!document.querySelector('.hdr-search.open')) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIdx((v) => Math.min(v + 1, items.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIdx((v) => Math.max(v - 1, 0));
      }
      if (e.key === 'Enter') {
        if (items[idx]) onPick(items[idx]);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [items, idx, onPick]);

  const renderThumb = (item) => {
    if ('href' in item) {
      if (item.type === 'grave') return <CustomIcon name="tombstone" lib="lucide" />;
      if (item.type === 'member') return <UserOutlined />;
      if (item.type === 'place') return <EnvironmentOutlined />;
      return <FileOutlined />;
    }
    return <ClockCircleOutlined />;
  };

  return (
    <div className="hdr-search__panel" role="listbox">
      <div className="hdr-search__body">
        {!hasQuery && recent.length > 0 && (
          <div className="hdr-search__section">
            <div className="hdr-search__section-hd">
              <span>
                <FormattedMessage id="search_recent_title" defaultMessage="Recent" />
              </span>
              <Button variant="text" size="small" onClick={onClearRecent}>
                <FormattedMessage id="search_recent_clear" defaultMessage="Clear" />
              </Button>
            </div>
            <div className="hdr-search__rows">
              {recent.map((r, i) => (
                <div
                  key={`${r.q}-${r.ts}`}
                  className={`hdr-search__row ${i === idx ? 'is-active' : ''}`}
                  role="option"
                  aria-selected={i === idx}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => onPick({ q: r.q })}
                >
                  <span className="hdr-search__thumb">{renderThumb(r)}</span>
                  <div className="hdr-search__meta">
                    <div className="hdr-search__title">{r.q}</div>
                    {r.type && <div className="hdr-search__sub">{r.type}</div>}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveRecent(r.q, r.type);
                    }}
                    aria-label={intl.formatMessage({
                      id: 'search_recent_remove',
                      defaultMessage: 'Remove',
                    })}
                    icon={<CloseOutlined size={16} />}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {hasQuery && loading && (
          <div className="hdr-search__section">
            <div className="hdr-search__section-hd">
              <span>
                <FormattedMessage id="search_suggestions_title" defaultMessage="Suggestions" />
              </span>
            </div>
            <div className="hdr-search__empty">
              <FormattedMessage id="search_searching" defaultMessage="Searching…" />
            </div>
          </div>
        )}

        {hasQuery && !loading && items.length === 0 && (
          <div className="hdr-search__section">
            <div className="hdr-search__empty">
              <FormattedMessage id="search_no_results" defaultMessage="No results" />
            </div>
          </div>
        )}

        {hasQuery &&
          !loading &&
          resultSections.map((section) => (
            <div className="hdr-search__section" key={section.key}>
              <div className="hdr-search__section-hd">
                <span>{section.title}</span>
              </div>
              <div className="hdr-search__rows">
                {section.entries.map(({ item: suggestion, index }) => (
                  <div
                    key={`${suggestion.type}-${suggestion.id}-${index}`}
                    className={`hdr-search__row ${index === idx ? 'is-active' : ''}`}
                    role="option"
                    aria-selected={index === idx}
                    onMouseEnter={() => setIdx(index)}
                    onClick={() => onPick(suggestion)}
                  >
                    <span className="hdr-search__thumb">{renderThumb(suggestion)}</span>
                    <div className="hdr-search__meta">
                      <div className="hdr-search__title">{suggestion.title}</div>
                      {suggestion.subtitle && (
                        <div className="hdr-search__sub">{suggestion.subtitle}</div>
                      )}
                    </div>
                    <span className="hdr-search__chev">
                      <RightOutlined size={16} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>

      {hasQuery && (
        <div className="hdr-search__footer">
          <Button type="primary" onClick={onOpenFull} size="small">
            <FormattedMessage id="search_open_full" defaultMessage="Search" />
          </Button>
        </div>
      )}
    </div>
  );
}
